import React from 'react';
import PropTypes from 'prop-types';
import { CommonLoginIdentityApp } from '@athena/common-login-identity-spa';
import '@athena/common-login-identity-spa/style.css';
import config from '../../lib/config';
import { useOktaAuth } from '@okta/okta-react';
import sessionStorageUtil from '../../lib/session-storage-util';
import { getTAndCShownCookie, setAndGetLwaUniqId } from '../../lib/cookie-utils';
import { getPostAuthorizeParams } from '../../lib/authorize-utils';
import { addHTI1Scopes } from '../../lib/scope-mapping-utils';
import { toCommonLoginProps } from './common-login-identity-adapter';

const POST_AUTHN_DEGRADE_REQUEST = 'postAuthnDegradeRequest';
const NON_DEGRADABLE_ERROR_ROUTE = `/error?message=${encodeURIComponent(
  "We're sorry, something went wrong. Please try again later."
)}`;

// OHSW's hash-routed entry points mapped to the SPA route that must render for them.
// Without this the SPA falls back to its own default ('/waiting').
const HASH_TO_INITIAL_ROUTE = {
  '#signin/athena-forgot-password': '/forgot-password',
  '#create': '/signup',
};

const defaultProps = {
  iamCoreUrl: config.iamCoreUrl,
  oktaUrl: config.oktaURL,
  redirectUri: config.redirectUri,
  amplitudeApiKey: config.amplitudeApiKey,
  scope: config.scopes.join(' '),
};

/**
 * On the post-authorize callback, the original POST /authorize params are nested in
 * the callback's state (same source PostAuthorize.js and AOSW's PostAuthnUtil.js read).
 * Returns undefined for GET flows so the replay stays a GET.
 */
function getPostAuthorizeReplayParams() {
  const { state } = getPostAuthorizeParams();
  try {
    const { authorizePostParamsObject } = JSON.parse(state);
    if (!authorizePostParamsObject || Object.keys(authorizePostParamsObject).length === 0) {
      return undefined;
    }
    return { ...authorizePostParamsObject, scope: addHTI1Scopes(authorizePostParamsObject.scope) };
  } catch (e) {
    return undefined;
  }
}

/**
 * Mounts <CommonLoginIdentityApp /> via its typed React props contract (ADR-002 in
 * common-login-identity-spa's design.md). All patient-workflow rendering, routing,
 * and /authorize replay live inside the imported component; this file only gathers
 * its inputs and wires onError.
 */
export default function CommonLoginIdentityWidget({ commonLoginIdentityInputs, ...postAuthnConfigs }) {
  const configs = { ...defaultProps, ...postAuthnConfigs };
  const { oktaAuth } = useOktaAuth();
  const commonLoginProps = toCommonLoginProps(configs, {
    oktaAuth,
    emailToken: commonLoginIdentityInputs?.emailToken,
    lwaUniqId: setAndGetLwaUniqId(),
    initialRoute: configs.nonDegradableError
      ? NON_DEGRADABLE_ERROR_ROUTE
      : HASH_TO_INITIAL_ROUTE[window.location.hash],
    tAndCShown: getTAndCShownCookie(),
    postAuthorizeParams: getPostAuthorizeReplayParams(),
  });

  function handleComplete() {
    // No-op: the SPA's AuthorizeReplayer performs the navigation / form submit itself.
  }

  function handleError({ kind, message }) {
    if (kind === 'session-expired') {
      const originalAuthUrl = sessionStorageUtil.getOriginalAuthUrl();
      if (originalAuthUrl) {
        window.location.assign(`${originalAuthUrl}&prompt=login`);
        return;
      }
    }
    // Same degrade pipeline PostAuthorize.js already listens on for AOSW errors.
    window.dispatchEvent(
      new CustomEvent(POST_AUTHN_DEGRADE_REQUEST, { detail: { kind, message } })
    );
  }

  return (
    <CommonLoginIdentityApp
      {...commonLoginProps}
      onComplete={handleComplete}
      onError={handleError}
    />
  );
}

CommonLoginIdentityWidget.propTypes = {
  commonLoginIdentityInputs: PropTypes.shape({
    emailToken: PropTypes.string,
  }),
  oktaUrl: PropTypes.string,
  iamCoreUrl: PropTypes.string,
  workflow: PropTypes.string,
  appType: PropTypes.string,
  logoUrl: PropTypes.string,
  logoText: PropTypes.string,
  clientId: PropTypes.string,
  redirectUri: PropTypes.string,
  scope: PropTypes.string,
  state: PropTypes.string,
  aud: PropTypes.string,
  username: PropTypes.string,
  idp: PropTypes.string,
  workflowHandlerEndpoint: PropTypes.string,
  displayName: PropTypes.string,
  workflowType: PropTypes.string,
  language: PropTypes.string,
  signupLink: PropTypes.string,
  callProcessCreds: PropTypes.bool,
  updateUserMfaProfile: PropTypes.bool,
  skipPatientSummary: PropTypes.bool,
  postAuthnRoutes: PropTypes.string,
  sanitizeInfo: PropTypes.string,
  userState: PropTypes.string,
  i18n: PropTypes.string,
  amplitudeApiKey: PropTypes.string,
  accessToken: PropTypes.string,
  idToken: PropTypes.string,
  nonDegradableError: PropTypes.bool,
  // JSON string, built by PreAuthorize.js via JSON.stringify(authorizePostParamsObject).
  authorizePostParams: PropTypes.string,
};
